import { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { useLocales } from 'src/locales';
import { titleCase } from 'src/utils/change-case';
import Iconify from 'src/components/iconify';
import CustomTooltip from './CustomTooltip';
import SerpModalButton from './SerpModalButton';

interface Props {
    items: any[];
    layout?: 'carousel' | 'grid';
    showBrandChip?: boolean;
}

export default function PlaCarousel({ items, layout = 'carousel', showBrandChip = false }: Props) {
    const displayItems = useMemo(() => items.slice(0, layout === 'grid' ? 60 : 60), [items, layout]);
    const { t } = useLocales();


    return (
        <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                        sx={{
                            color: '#333C47',
                            fontFamily: '"Public Sans", sans-serif',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '120%',
                        }}
                    >
                        {titleCase(t("dashboard.global.visibility.top_product_listing_ads"))}
                    </Typography>
                </Stack>
                <Typography variant="caption" fontSize={14} color="text.secondary">{items.length} {t("dashboard.global.visibility.results_found")}</Typography>
            </Stack>
            {items.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.neutral' }}>
                    <Typography variant="body2" color="text.secondary">No PLA ads found for this country</Typography>
                </Paper>
            ) : (
                <Box sx={layout === 'grid' ? {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: 1.5,
                } : {
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    gap: 1.5,
                    pb: 1,
                    pr: 1,
                    scrollbarWidth: 'thin',
                    msOverflowStyle: 'auto',
                    '&::-webkit-scrollbar': { height: 8 },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        borderRadius: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                }}>
                    {displayItems.map((ad, idx) => (
                        <Paper
                            key={idx}
                            variant="outlined"
                            sx={{
                                p: 0,
                                backgroundColor: 'background.paper',
                                height: '251px',
                                display: 'flex',
                                flexDirection: 'column',
                                ...(layout === 'carousel' && {
                                    minWidth: 250,
                                    maxWidth: 250,
                                    overflow: 'hidden',
                                }),
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    height: 76,
                                    borderRadius: '10px 10px 0 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    flexShrink: 0,
                                }}
                            >
                                <Box sx={{
                                    position: 'absolute',
                                    height: 16,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '16px',
                                    top: 6,
                                    right: 6,
                                    zIndex: 1,
                                    backgroundColor: 'action.hover',
                                    px: 0.5
                                }}>
                                    <CustomTooltip title={`Search term: ${ad.searchTerm}`} placement="top">
                                        <svg style={{ cursor: 'help' }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <g clip-path="url(#clip0_3494_15959)">
                                                <rect width="16" height="16" rx="8" fill="white" />
                                                <path d="M8.00016 10.6673V8.00065M8.00016 5.33398H8.00683M14.6668 8.00065C14.6668 11.6825 11.6821 14.6673 8.00016 14.6673C4.31826 14.6673 1.3335 11.6825 1.3335 8.00065C1.3335 4.31875 4.31826 1.33398 8.00016 1.33398C11.6821 1.33398 14.6668 4.31875 14.6668 8.00065Z" stroke="#5E6D7B" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_3494_15959">
                                                    <rect width="16" height="16" rx="8" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </CustomTooltip>
                                </Box>
                                {ad.img ? (
                                    <Box component="img" src={ad.img} alt={ad.title} sx={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'white', transform: 'scale(1.4)', transformOrigin: 'center center' }} />
                                ) : (
                                    <Iconify icon="mdi:image-outline" />
                                )}
                            </Box>
                            <Stack
                                spacing={0}
                                p={0}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                }}
                            >
                                <Box
                                    p={1}
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box sx={{
                                        position: 'relative',
                                        ...(showBrandChip && (ad as any).brandInfringementTitle && {
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                width: '120px',
                                                height: '16px',
                                                background: 'linear-gradient(90deg, transparent, background.paper 40%)',
                                                pointerEvents: 'none'
                                            }
                                        })
                                    }}>
                                        <Typography
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                displayPrint: 'block',

                                                color: '#333C47',
                                                fontFamily: '"Public Sans", sans-serif',
                                                fontSize: '14px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '120%',
                                            }}
                                        >
                                            {ad.title}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography
                                            sx={{
                                                color: '#637381',
                                                fontFamily: '"Public Sans", sans-serif',
                                                fontSize: '14px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '120%',
                                                mt: '6px',
                                            }}
                                        >
                                            {ad.price}
                                        </Typography>

                                        {showBrandChip && ((ad as any).brandInfringementTitle || (ad as any).brandInfringementDescription) && (
                                            <Chip label="Brand" color="error" size="small" sx={{ height: 16 }} />
                                        )}
                                    </Stack>
                                </Box>
                                <Box
                                    sx={{
                                        borderRadius: '10px',
                                        borderTop: '1px solid divider',
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box px={1} pt={1}>
                                        <Stack direction="column" alignItems="space-between" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title={t('definitions.position')}
                                                >
                                                    <Stack direction="row" alignItems="center" gap="4px">
                                                        <Typography
                                                            sx={{
                                                                color: '#919EAB',
                                                                fontFamily: '"Public Sans", sans-serif',
                                                                fontSize: '13px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 400,
                                                                lineHeight: '140%',
                                                                cursor: 'help',
                                                            }}
                                                        >
                                                            Position
                                                        </Typography>

                                                        <svg
                                                            style={{ cursor: 'help' }}
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 12 12"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                                stroke="#637381"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </Stack>
                                                </Tooltip>
                                                <Typography
                                                    sx={{
                                                        color: '#637381',
                                                        fontFamily: '"Public Sans", sans-serif',
                                                        fontSize: '14px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '140%',
                                                    }}
                                                >
                                                    {ad.position}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} justifyContent="space-between">
                                                <Tooltip
                                                    arrow
                                                    placement="top"
                                                    title={t('definitions.frequency')}
                                                >
                                                    <Stack direction="row" alignItems="center" gap="4px">
                                                        <Typography
                                                            sx={{
                                                                color: '#919EAB',
                                                                fontFamily: '"Public Sans", sans-serif',
                                                                fontSize: '13px',
                                                                fontStyle: 'normal',
                                                                fontWeight: 400,
                                                                lineHeight: '140%',
                                                                cursor: 'help',
                                                            }}
                                                        >
                                                            Frequency
                                                        </Typography>

                                                        <svg
                                                            style={{ cursor: 'help' }}
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 12 12"
                                                            fill="none"
                                                        >
                                                            <path
                                                                d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                                stroke="#637381"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </Stack>
                                                </Tooltip>
                                                <Typography
                                                    sx={{
                                                        color: '#637381',
                                                        fontFamily: '"Public Sans", sans-serif',
                                                        fontSize: '14px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '140%',
                                                    }}
                                                >
                                                    {ad.frequency}%
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                    <Paper
                                        square
                                        elevation={0}
                                        sx={{
                                            mt: '12px',
                                            borderTop: '1px solid #E3E7EA',
                                            background: '#F8F9FA',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '8px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Tooltip title={`Search term: ${ad.searchTerm}`} placement="top">
                                            <SerpModalButton
                                                gcsUrl={
                                                    ad.device_type === 'MOBILE'
                                                        ? (ad as any)._gcs_uri_mobile
                                                        : (ad as any)._gcs_uri_desktop
                                                }
                                                deviceType={ad.device_type}
                                                iconType={ad.device_type === 'MOBILE' ? 'mobile' : 'desktop'}
                                                searchTerm={ad.searchTerm}
                                            />
                                        </Tooltip>
                                    </Paper>
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Box>
            )}
        </Stack>
    );
}